import {
  useCallback,
  useRef,
  type ForwardedRef,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";

import { clamp, sliderThumbSizes, type RangeSliderThumb, type SliderSize } from "./sliderMath";

/**
 * 컨트롤드 네이티브 input 값을 프로그램적으로 바꾸고 input 이벤트를 발생시켜
 * React onChange(=RangeSlider 의 thumb 클램프 로직)를 그대로 태우기 위한 setter.
 */
const nativeInputValueSetter =
  typeof HTMLInputElement !== "undefined"
    ? Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set
    : undefined;

export interface UseRangeSliderInteractionOptions {
  min: number;
  max: number;
  startValue: number;
  endValue: number;
  resolvedStep: number | "any";
  thumbSize: SliderSize;
  disabled: boolean;
  /** RangeSlider 가 forwardRef 로 받은 ref — start thumb 에 연결한다. */
  forwardedRef: ForwardedRef<HTMLInputElement>;
}

export interface RangeSliderInteraction {
  startRef: RefObject<HTMLInputElement | null>;
  endRef: RefObject<HTMLInputElement | null>;
  stackRef: RefObject<HTMLDivElement | null>;
  setStartRef: (node: HTMLInputElement | null) => void;
  handleTrackPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
}

/**
 * RangeSlider 의 트랙 클릭/드래그 인터랙션과 thumb ref 관리를 분리한 headless 훅.
 * thumb 값 갱신은 native input 이벤트를 디스패치해 RangeSlider 의 onChange 클램프 경로를
 * 그대로 태우므로, 간격/교차 보정 로직은 컴포넌트 한 곳에만 존재한다.
 */
export function useRangeSliderInteraction({
  min,
  max,
  startValue,
  endValue,
  resolvedStep,
  thumbSize,
  disabled,
  forwardedRef,
}: UseRangeSliderInteractionOptions): RangeSliderInteraction {
  const startRef = useRef<HTMLInputElement | null>(null);
  const endRef = useRef<HTMLInputElement | null>(null);
  const stackRef = useRef<HTMLDivElement | null>(null);

  const setStartRef = useCallback(
    (node: HTMLInputElement | null) => {
      startRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) (forwardedRef as RefObject<HTMLInputElement | null>).current = node;
    },
    [forwardedRef],
  );

  // 트랙 클릭 위치 → 값. thumb 인셋(thumbCenter)과 일치하도록 역산.
  const computeTrackValue = useCallback(
    (clientX: number) => {
      const stack = stackRef.current;
      if (!stack) return null;
      const rect = stack.getBoundingClientRect();
      const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const thumbPx = parseFloat(sliderThumbSizes[thumbSize]) * rootFont;
      const usable = rect.width - thumbPx;
      const offset = clamp(clientX - rect.left - thumbPx / 2, 0, Math.max(usable, 0));
      const fraction = usable > 0 ? offset / usable : 0;
      let next = min + fraction * (max - min);
      if (typeof resolvedStep === "number" && resolvedStep > 0) {
        next = Math.round((next - min) / resolvedStep) * resolvedStep + min;
      }
      return clamp(next, min, max);
    },
    [max, min, resolvedStep, thumbSize],
  );

  const setThumbValue = useCallback((thumb: RangeSliderThumb, next: number) => {
    const input = thumb === "start" ? startRef.current : endRef.current;
    if (!input || !nativeInputValueSetter) return;
    nativeInputValueSetter.call(input, String(next));
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }, []);

  // 트랙 클릭: 가까운 쪽 thumb 를 클릭 위치로 점프 + 누른 채로 이어서 드래그.
  const handleTrackPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || event.button !== 0) return;
    if (event.target instanceof HTMLInputElement) return; // thumb 직접 잡음 → native drag
    const next = computeTrackValue(event.clientX);
    if (next == null) return;

    const thumb: RangeSliderThumb =
      next < startValue
        ? "start"
        : next > endValue
          ? "end"
          : Math.abs(next - startValue) <= Math.abs(next - endValue)
            ? "start"
            : "end";

    setThumbValue(thumb, next);
    (thumb === "start" ? startRef.current : endRef.current)?.focus();

    const handleMove = (moveEvent: PointerEvent) => {
      const moved = computeTrackValue(moveEvent.clientX);
      if (moved != null) setThumbValue(thumb, moved);
    };
    const handleUp = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  };

  return { startRef, endRef, stackRef, setStartRef, handleTrackPointerDown };
}
